/**
 * Animated title-screen background.
 *
 * Renders a Tron-grid + parallax hill silhouettes + scrolling sin-wave terrain
 * + ambient motes. Palette is randomised per-run from a single base hue —
 * foreground is bright and saturated, hills fade toward the horizon in
 * progressively desaturated, lightened tints (fog/haze effect).
 *
 * Designed to run whenever the game itself isn't — title, level select,
 * results, settings. Stopped during gameplay so the Game instance owns
 * the canvas exclusively.
 */

import { Renderer } from '@/rendering/renderer';
import type { Color } from '@/types';
import { hslToColor, deriveHillPalette } from '@/types/color-palette';

const GROUND_Y = 80;
const SCROLL_SPEED = 140;
const PULSE_BPM = 108;

interface Palette {
  base: Color;
  accent: Color;
  hills: Color[];     // [farthest → nearest]
  moteWarm: Color;
  moteCool: Color;
  background: Color;
}

interface Hill {
  centerY: number;
  amplitude: number;
  frequency: number;
  phase: number;
  parallax: number;   // 0=static, 1=full scroll
  color: Color;
}

export class TitleBackground {
  private renderer: Renderer | null = null;
  private canvas: HTMLCanvasElement;
  private rafId: number | null = null;
  private lastT = 0;
  private scrollX = 0;
  private time = 0;
  private running = false;
  private palette: Palette = buildPalette(0.55);
  private hills: Hill[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
  }

  start(): void {
    if (this.running) return;
    if (!this.renderer) this.renderer = new Renderer(this.canvas);
    this.palette = buildPalette(Math.random());
    this.hills = buildHills(this.palette);
    this.renderer.bloomStrength = 0.9;
    this.renderer.vignetteIntensity = 0.4;
    this.running = true;
    this.lastT = performance.now();
    this.rafId = requestAnimationFrame(this.loop);
  }

  stop(): void {
    this.running = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private loop = (): void => {
    if (!this.running || !this.renderer) return;
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastT) / 1000);
    this.lastT = now;
    this.time += dt;
    this.scrollX += dt * SCROLL_SPEED;
    this.render();
    this.rafId = requestAnimationFrame(this.loop);
  };

  private render(): void {
    const r = this.renderer!;
    const cam = r.camera;
    cam.position.x = this.scrollX;
    cam.position.y = GROUND_Y + 60;

    const beat = (this.time * PULSE_BPM) / 60;
    const phase = beat - Math.floor(beat);
    const pulse = Math.pow(1 - phase, 3);
    r.beatPulse = pulse * 0.25;
    r.chromaticAberration = 0.0015 + pulse * 0.004;

    r.beginFrame(this.palette.background);

    // Back-to-front: hills farthest first so nearer layers occlude.
    for (const hill of this.hills) this.drawHill(hill);

    r.drawGrid(GROUND_Y, this.palette.base, 50, 0.55 + pulse * 0.25);

    this.drawTerrain();
    this.drawMotes();

    r.endFrame();
  }

  private drawHill(hill: Hill): void {
    const r = this.renderer!;
    const cam = r.camera;
    const step = 8;
    const worldLeft = cam.position.x - cam.viewWidth;
    const worldRight = cam.position.x + cam.viewWidth;
    const bottomY = GROUND_Y - 400;

    const parallaxShift = this.scrollX * (1 - hill.parallax);
    const height = (x: number) =>
      hill.centerY + hill.amplitude * Math.sin((x + parallaxShift) * hill.frequency + hill.phase);

    let prevX = worldLeft;
    let prevY = height(prevX);
    for (let x = worldLeft + step; x <= worldRight + step; x += step) {
      const y = height(x);
      r.drawTriangle(prevX, prevY, x, y, prevX, bottomY, hill.color);
      r.drawTriangle(x, y, x, bottomY, prevX, bottomY, hill.color);
      prevX = x;
      prevY = y;
    }
  }

  private drawTerrain(): void {
    const r = this.renderer!;
    const cam = r.camera;
    const step = 6;
    const worldLeft = cam.position.x - cam.viewWidth;
    const worldRight = cam.position.x + cam.viewWidth;
    const fill: Color = { ...this.palette.base, a: 0.35 };
    const line: Color = { ...this.palette.base, a: 1.0 };
    const bottomY = GROUND_Y - 400;

    const ground = (x: number) =>
      GROUND_Y + 30 * Math.sin(x * 0.012) + 10 * Math.sin(x * 0.037 + 1.3);

    let prevX = worldLeft;
    let prevY = ground(prevX);
    for (let x = worldLeft + step; x <= worldRight + step; x += step) {
      const y = ground(x);
      r.drawTriangle(prevX, prevY, x, y, prevX, bottomY, fill);
      r.drawTriangle(x, y, x, bottomY, prevX, bottomY, fill);
      r.drawLine(prevX, prevY, x, y, 2, line);
      prevX = x;
      prevY = y;
    }
  }

  private drawMotes(): void {
    const r = this.renderer!;
    const cam = r.camera;
    const count = 14;
    for (let i = 0; i < count; i++) {
      const seed = i * 137.17;
      const periodX = cam.viewWidth * 2 + 240;
      const localX = ((this.scrollX * 0.35 + seed) % periodX + periodX) % periodX;
      const x = cam.position.x - cam.viewWidth + localX;
      const y = GROUND_Y + 120 + 70 * Math.sin(this.time * 0.6 + i * 0.9);
      const alpha = 0.4 + 0.3 * Math.sin(this.time * 1.3 + i);
      const base = i % 3 === 0 ? this.palette.moteWarm : this.palette.moteCool;
      r.drawRect(x - 2, y - 2, 4, 4, { ...base, a: alpha });
    }
  }
}

// ─── Palette construction ───────────────────────────────────

function buildPalette(hue01: number): Palette {
  const h = hue01;
  const accentHue = (h + 0.5) % 1;

  return {
    base:       hslToColor(h,         0.95, 0.62),
    accent:     hslToColor(accentHue, 0.9,  0.72),
    hills:      deriveHillPalette(h),
    moteWarm:   hslToColor(accentHue, 1.0, 0.7),
    moteCool:   hslToColor(h,         0.9, 0.75),
    background: hslToColor(h,         0.5, 0.09),
  };
}

function buildHills(p: Palette): Hill[] {
  return [
    { centerY: 280, amplitude: 40, frequency: 0.003, phase: 0.0, parallax: 0.15, color: p.hills[0] },
    { centerY: 200, amplitude: 55, frequency: 0.005, phase: 1.7, parallax: 0.35, color: p.hills[1] },
    { centerY: 140, amplitude: 50, frequency: 0.008, phase: 3.4, parallax: 0.6,  color: p.hills[2] },
  ];
}

